import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import styled from 'styled-components';
import { Search, BarChart3, X, ExternalLink, BookOpen } from 'lucide-react';
import {
  clearTimelineNavigationState,
  readTimelineNavigationState,
  saveTimelineNavigationState
} from '../utils/timelineNavigationState';

const TimelineContainer = styled.div`
  min-height: 100vh;
  background: var(--color-background);
`;

const Header = styled.section`
  background: var(--gradient-primary);
  color: white;
  padding: var(--spacing-2xl) 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: var(--spacing-xl) 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Controls = styled.div`
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding: var(--spacing-lg) 0;
  position: sticky;
  top: 70px;
  z-index: var(--z-sticky);

  @media (max-width: 768px) {
    position: static;
    padding: var(--spacing-md) 0;
  }
`;

const ControlsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
    gap: var(--spacing-md);
  }
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    flex-basis: 100%;
    min-width: 100%;
  }
  
  .search-icon {
    position: absolute;
    left: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #D1D1D6;
  border-radius: 10px;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  background: #FFFFFF;
  color: #1D1D1F;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #8E8E93;
    font-weight: 400;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 10px 16px;
  border: 1px solid #D1D1D6;
  border-radius: 10px;
  background: #FFFFFF;
  color: #1D1D1F;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  white-space: nowrap;

  @media (max-width: 768px) {
    justify-content: center;
    min-width: 0;
    width: 100%;
  }
  
  &:hover {
    border-color: #007AFF;
    color: #007AFF;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
    transform: translateY(-1px);
  }
  
  &.active {
    border-color: #007AFF;
    background: #007AFF;
    color: white;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      border-color: #D1D1D6;
      color: #1D1D1F;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PeriodFilterGroup = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--spacing-sm);
  }
`;

const SelectorGroup = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--spacing-sm);
  }
`;

const TickIntervalSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    justify-content: space-between;
    min-width: 0;
    width: 100%;
  }
  
  label {
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1D1D1F;
    white-space: nowrap;
  }
  
  select {
    padding: 8px 12px;
    border: 1px solid #D1D1D6;
    border-radius: 8px;
    background: #FFFFFF;
    color: #1D1D1F;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 80px;
    max-width: 100%;
    
    &:focus {
      outline: none;
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }
    
    &:hover {
      border-color: #007AFF;
    }
  }
`;

const FishboneToggle = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-background);
  color: var(--color-text-secondary);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    justify-content: center;
    min-width: 0;
    width: 100%;
  }
  
  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  
  &.active {
    border-color: var(--color-primary);
    background: var(--color-primary);
    color: white;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 16px;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-column: 1 / -1;
    margin-left: 0;
  }
`;

const NavButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #D1D1D6;
  border-radius: 8px;
  background: #FFFFFF;
  color: #1D1D1F;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 8px 6px;
    min-width: 0;
    width: 100%;
  }
  
  &:hover:not(:disabled) {
    border-color: #007AFF;
    color: #007AFF;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RangeSummary = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #D1D1D6;
  border-radius: 8px;
  background: #FFFFFF;
  color: #4A5568;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    justify-content: center;
    width: 100%;
  }
`;

const TimelineSection = styled.section`
  padding: var(--spacing-2xl) 0;

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const TimelineContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);

  @media (max-width: 768px) {
    padding: 0 var(--spacing-md);
  }
`;

const TimelineVisualization = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E5E7;
  border-radius: 16px;
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  overflow: hidden;
  position: relative;
  user-select: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(20px);

  @media (max-width: 768px) {
    border-radius: 12px;
    padding: var(--spacing-md);
  }
  
  &::before {
    content: '💡 使用下方滑块或拖拽查看时间轴，点击条目查看详情';
    position: absolute;
    top: 16px;
    right: 16px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 10px 14px;
    border-radius: 10px;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    font-size: 11px;
    font-weight: 500;
    z-index: 10;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
    letter-spacing: 0.2px;
  }
  
  
  &.fishbone::before {
    content: '🐟 左右拖拽查看鱼骨时间轴，点击条目查看详情';
  }
  
  &:hover::before {
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    &::before {
      display: none;
    }
  }
  
  .timeline-svg {
    width: 100%;
    min-height: 400px;
    cursor: grab;

    @media (max-width: 768px) {
      min-height: 280px;
    }
    
    &:active {
      cursor: grabbing;
    }
  }
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  margin-bottom: var(--spacing-md);
  background: #f2f2f7;
  color: #4a5568;
  border: 1px solid #e5e5ea;
  padding: 8px 12px;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.3px;
  line-height: 1.4;
  white-space: normal;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-bottom: var(--spacing-sm);
    text-align: center;
  }
`;

const MobileTimelineHint = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-top: var(--spacing-sm);
    color: #4A5568;
    font-size: 12px;
    line-height: 1.5;
    text-align: center;
  }
`;

const TimelineSliderContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.04);
  z-index: 5;

  @media (max-width: 768px) {
    position: static;
    height: auto;
    margin-top: var(--spacing-md);
    padding: var(--spacing-md);
    display: grid;
    gap: var(--spacing-md);
  }
`;

// 全景图容器
const OverviewContainer = styled.div`
  position: absolute;
  bottom: 90px;
  left: 20px;
  right: 20px;
  height: 80px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.04);
  z-index: 4;
  overflow: hidden;
  padding: 10px;

  @media (max-width: 768px) {
    position: static;
    height: 72px;
    margin-top: var(--spacing-md);
  }
`;

// 全景图SVG
const OverviewSvg = styled.svg`
  width: 100%;
  height: 100%;
  cursor: pointer;
  
  .overview-dynasty {
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      stroke-width: 2;
      stroke: #007AFF;
    }
  }
  
  .overview-viewport {
    fill: rgba(0, 122, 255, 0.2);
    stroke: #007AFF;
    stroke-width: 2;
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
  }
`;

// 全景图标签
const OverviewLabel = styled.div`
  position: absolute;
  top: -25px;
  left: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #1D1D1F;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 6px;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    position: static;
    margin-bottom: 6px;
    padding: 0;
    background: transparent;
    backdrop-filter: none;
  }
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: #E5E5E7;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #D1D1D6;
  }

  @media (max-width: 768px) {
    height: 6px;
    margin-bottom: 22px;
  }
`;

const SliderProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, #007AFF 0%, #5AC8FA 100%);
  border-radius: 2px;
  transition: width 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const SliderThumb = styled.div`
  position: absolute;
  top: 50%;
  width: 24px;
  height: 24px;
  background: #FFFFFF;
  border: 2px solid #007AFF;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  cursor: grab;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 4px 16px rgba(0, 122, 255, 0.3), 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    cursor: grabbing;
    transform: translate(-50%, -50%) scale(1.05);
    border-color: #0056CC;
  }
`;

const SliderLabels = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8E8E93;

  span {
    white-space: nowrap;
  }

  .mobile-label {
    display: none;
  }

  @media (max-width: 768px) {
    font-size: 10px;

    .desktop-label {
      display: none;
    }

    .mobile-label {
      display: inline;
    }
  }
`;

const SliderControlButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-left: 20px;

  @media (max-width: 768px) {
    justify-content: center;
    margin-left: 0;
  }
`;

const SliderButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(0, 122, 255, 0.1);
  color: #007AFF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 768px) {
    width: 48px;
    height: 40px;
  }
  
  &:hover {
    background: rgba(0, 122, 255, 0.15);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

const DynastyInfo = styled(motion.div)`
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  
  .dynasty-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--spacing-lg);
  }
  
  .dynasty-period {
    color: var(--color-text-secondary);
    font-size: 1rem;
    margin-top: var(--spacing-sm);
  }
  
  .close-button {
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-md);
    padding: var(--spacing-sm);
    cursor: pointer;
    color: var(--color-text-muted);
    transition: all var(--animation-duration) ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: var(--color-background);
      color: var(--color-text-primary);
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
  
  .dynasty-events {
    margin: var(--spacing-lg) 0;
    
    h4 {
      color: var(--color-text-primary);
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: var(--spacing-md);
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        padding: var(--spacing-sm) 0;
        border-bottom: 1px solid var(--color-border);
        color: var(--color-text-secondary);
        line-height: 1.5;
        
        &:last-child {
          border-bottom: none;
        }
        
        strong {
          color: var(--color-primary);
        }
      }
    }
  }
  
  .dynasty-actions {
    display: flex;
    gap: var(--spacing-md);
    margin-top: var(--spacing-lg);
  }
  
  .action-button {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--color-primary);
    color: white;
    border: none;
    border-radius: var(--border-radius-md);
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: all var(--animation-duration) ease;
    
    &:hover {
      background: var(--color-primary-dark);
      transform: translateY(-2px);
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: var(--color-text-muted);
  font-size: 1rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--color-text-muted);
`;

const appleColors = {
  blue: '#B8D4F0',
  green: '#C8E6C9',
  indigo: '#D1C4E9',
  orange: '#FFE0B2',
  pink: '#F8BBD9',
  purple: '#E1BEE7',
  red: '#FFCDD2',
  teal: '#B2DFDB',
  yellow: '#FFF9C4',
  brown: '#D7CCC8',
  deepOrange: '#FFCCBC',
  lightBlue: '#B3E5FC',
  lime: '#F0F4C3',
  amber: '#FFECB3'
};

function Timeline() {
  const navigate = useNavigate();
  const svgRef = useRef();
  const restoredStateRef = useRef();
  const hasRestoredSnapshotRef = useRef(false);

  if (restoredStateRef.current === undefined) {
    restoredStateRef.current = readTimelineNavigationState();
  }

  const restoredState = restoredStateRef.current;
  const restoredNumber = (value, fallback) => Number.isFinite(value) ? value : fallback;
  const restoredPeriod = ['all', '古代', '近现代'].includes(restoredState?.selectedPeriod)
    ? restoredState.selectedPeriod
    : 'all';
  const restoredTickInterval = [50, 100, 200, 500].includes(restoredState?.tickInterval)
    ? restoredState.tickInterval
    : 100;
  const restoredColumnCount = Number.isInteger(restoredState?.columnCount)
    ? Math.max(1, Math.min(5, restoredState.columnCount))
    : 3;
  
  const [dynasties, setDynasties] = useState([]);
  const [filteredDynasties, setFilteredDynasties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(
    typeof restoredState?.searchTerm === 'string' ? restoredState.searchTerm : ''
  );
  const [selectedPeriod, setSelectedPeriod] = useState(restoredPeriod);
  const [selectedDynasty, setSelectedDynasty] = useState(null);
  const [currentDynastyIndex, setCurrentDynastyIndex] = useState(
    Math.max(0, Math.trunc(restoredNumber(restoredState?.currentDynastyIndex, 0)))
  );
  const [dragOffset, setDragOffset] = useState(restoredNumber(restoredState?.dragOffset, 0));
  const [tickInterval, setTickInterval] = useState(restoredTickInterval);
  const [showFishbone, setShowFishbone] = useState(restoredState?.showFishbone === true);
  const [columnCount, setColumnCount] = useState(restoredColumnCount);
  const [sliderValue, setSliderValue] = useState(
    Math.max(0, Math.min(100, restoredNumber(restoredState?.sliderValue, 0)))
  );
  const [timelineRange, setTimelineRange] = useState({ min: 0, max: 100 });
  const sliderRef = useRef();
  const overviewRef = useRef();
  
  // 触摸滑动相关状态
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastDragX, setLastDragX] = useState(0);

  const periods = [
    { value: 'all', label: '全部' },
    { value: '古代', label: '古代' },
    { value: '近现代', label: '近现代' }
  ];

  // 统一年份显示（支持公元/公元前）
  const formatYear = useCallback((year) => {
    if (typeof year !== 'number' || Number.isNaN(year)) return String(year);
    return year < 0 ? `公元前${Math.abs(year)}年` : `公元${year}年`;
  }, []);

  const formatShortYear = useCallback((year) => {
    if (typeof year !== 'number' || Number.isNaN(year)) return String(year);
    return year < 0 ? `前${Math.abs(year)}` : `${year}`;
  }, []);

  const fetchDynasties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/dynasties');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      let data;
      
      if (result && typeof result === 'object') {
        if (result.success && Array.isArray(result.data)) {
          data = result.data;
        } else if (Array.isArray(result)) {
          data = result;
        } else {
          console.error('API返回的数据格式不正确:', result);
          setError('数据格式错误');
          setDynasties([]);
          return;
        }
      } else {
        console.error('API返回的数据不是对象:', result);
        setError('数据格式错误');
        setDynasties([]);
        return;
      }
      
      const processedData = data.map((dynasty) => {
        const startYear = dynasty.startYear ?? dynasty.start_year;
        const endYear = dynasty.endYear ?? dynasty.end_year;
        return {
          ...dynasty,
          startYear,
          endYear,
          period: dynasty.period || '古代'
        };
      });
      
      setDynasties(processedData);
    } catch (err) {
      console.error('获取朝代数据失败:', err);
      setError(err.message || '获取数据失败');
      setDynasties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterDynasties = useCallback(() => {
    if (!Array.isArray(dynasties)) {
      setFilteredDynasties([]);
      return;
    }
    
    let filtered = dynasties.filter(dynasty => {
      const matchesSearch = !searchTerm || 
        dynasty.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dynasty.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPeriod = selectedPeriod === 'all' || dynasty.period === selectedPeriod;
      
      return matchesSearch && matchesPeriod;
    });
    
    filtered.sort((a, b) => (a.startYear || 0) - (b.startYear || 0));
    setFilteredDynasties(filtered);
    
    if (filtered.length > 0) {
      const minYear = Math.min(...filtered.map(d => d.startYear || 0));
      const maxYear = Math.max(...filtered.map(d => d.endYear || 0));
      setTimelineRange({ min: minYear, max: maxYear });
    }
  }, [dynasties, searchTerm, selectedPeriod]);

  const calculateSliderPosition = useCallback(() => {
    if (!filteredDynasties.length) return 0;
    const totalRange = timelineRange.max - timelineRange.min;
    const currentOffset = Math.abs(dragOffset);
    const maxOffset = Math.max(0, totalRange / tickInterval * 100 - 800);
    return maxOffset > 0 ? (currentOffset / maxOffset) * 100 : 0;
  }, [dragOffset, timelineRange, tickInterval, filteredDynasties.length]);

  const handleSliderChange = useCallback((newValue) => {
    if (!filteredDynasties.length) return;
    const totalRange = timelineRange.max - timelineRange.min;
    const maxOffset = Math.max(0, totalRange / tickInterval * 100 - 800);
    const newOffset = -(maxOffset * newValue / 100);
    setDragOffset(newOffset);
    setSliderValue(newValue);
  }, [filteredDynasties.length, timelineRange, tickInterval]);

  const handleSliderStep = useCallback((direction) => {
    const step = 5;
    const newValue = Math.max(0, Math.min(100, sliderValue + (direction * step)));
    handleSliderChange(newValue);
  }, [sliderValue, handleSliderChange]);

  const visibleRange = useMemo(() => {
    const totalRange = timelineRange.max - timelineRange.min;
    if (!filteredDynasties.length || totalRange <= 0) return null;

    const windowSpan = Math.min(totalRange, tickInterval * 8);
    const start = Math.round(timelineRange.min + ((totalRange - windowSpan) * sliderValue / 100));
    return {
      start,
      end: Math.round(Math.min(timelineRange.max, start + windowSpan))
    };
  }, [filteredDynasties.length, sliderValue, tickInterval, timelineRange]);

  const jumpToStart = useCallback(() => {
    handleSliderChange(0);
    setCurrentDynastyIndex(0);
    setSelectedDynasty(filteredDynasties[0] || null);
  }, [filteredDynasties, handleSliderChange]);

  const jumpToRecent = useCallback(() => {
    const lastIndex = Math.max(0, filteredDynasties.length - 1);
    handleSliderChange(100);
    setCurrentDynastyIndex(lastIndex);
    setSelectedDynasty(filteredDynasties[lastIndex] || null);
  }, [filteredDynasties, handleSliderChange]);

  const resetTimeline = useCallback(() => {
    setSearchTerm('');
    setSelectedPeriod('all');
    setSelectedDynasty(null);
    setCurrentDynastyIndex(0);
    setDragOffset(0);
    setSliderValue(0);
  }, []);

  const handleSliderMouseDown = useCallback((e) => {
    if (!sliderRef.current) return;
    
    const startX = e.clientX;
    const startValue = sliderValue;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / sliderRef.current.offsetWidth) * 100;
      const newValue = Math.max(0, Math.min(100, startValue + deltaPercent));
      handleSliderChange(newValue);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sliderValue, handleSliderChange]);

  const handleTrackClick = useCallback((e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newValue = (clickX / rect.width) * 100;
    handleSliderChange(Math.max(0, Math.min(100, newValue)));
  }, [handleSliderChange]);

  useEffect(() => {
    if (!filteredDynasties.length) return;
    const newPosition = calculateSliderPosition();
    setSliderValue(newPosition);
  }, [calculateSliderPosition, filteredDynasties.length]);

  // 全景图绘制函数
  const drawOverview = useCallback(() => {
    if (!overviewRef.current || !filteredDynasties.length) return;
    
    const svg = d3.select(overviewRef.current);
    svg.selectAll('*').remove();
    
    const width = overviewRef.current.clientWidth;
    
    const minYear = Math.min(...filteredDynasties.map(d => d.startYear || 0));
    const maxYear = Math.max(...filteredDynasties.map(d => d.endYear || 0));
    
    const xScale = d3.scaleLinear()
      .domain([minYear, maxYear])
      .range([10, width - 10]);
    
    const colorScale = d3.scaleOrdinal(Object.values(appleColors));
    
    // 绘制朝代矩形
    svg.selectAll('.overview-dynasty')
      .data(filteredDynasties)
      .enter()
      .append('rect')
      .attr('class', 'overview-dynasty')
      .attr('x', d => xScale(d.startYear))
      .attr('y', 20)
      .attr('width', d => Math.max(xScale(d.endYear) - xScale(d.startYear), 2))
      .attr('height', 20)
      .attr('fill', (d, i) => colorScale(i))
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1)
      .attr('rx', 2)
      .on('click', function(event, d) {
        // 点击朝代跳转到对应位置
        const targetYear = d.startYear;
        const yearRange = maxYear - minYear;
        const newSliderValue = ((targetYear - minYear) / yearRange) * 100;
        handleSliderChange(Math.max(0, Math.min(100, newSliderValue)));
      });
    
    // 绘制视口指示器
    const viewportWidth = (width - 20) * 0.3; // 假设视口占30%
    const viewportX = 10 + (sliderValue / 100) * (width - 20 - viewportWidth);
    
    svg.append('rect')
      .attr('class', 'overview-viewport')
      .attr('x', viewportX)
      .attr('y', 15)
      .attr('width', viewportWidth)
      .attr('height', 30)
      .attr('rx', 4)
      .call(d3.drag()
        .on('start', function() {
          setIsDragging(true);
        })
        .on('drag', function(event) {
          const newX = Math.max(10, Math.min(width - 10 - viewportWidth, event.x - viewportWidth / 2));
          const newSliderValue = ((newX - 10) / (width - 20 - viewportWidth)) * 100;
          handleSliderChange(Math.max(0, Math.min(100, newSliderValue)));
        })
        .on('end', function() {
          setIsDragging(false);
        })
      );
  }, [filteredDynasties, sliderValue, handleSliderChange]);

  // 触摸滑动事件处理
  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setLastDragX(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStart) return;
    
    const currentX = e.targetTouches[0].clientX;
    const deltaX = currentX - lastDragX;
    
    // 计算新的拖拽偏移
    const newDragOffset = dragOffset + deltaX;
    setDragOffset(newDragOffset);
    setLastDragX(currentX);
    
    // 同步更新滑块
    if (filteredDynasties.length > 0) {
      const minYear = Math.min(...filteredDynasties.map(d => d.startYear || 0));
      const maxYear = Math.max(...filteredDynasties.map(d => d.endYear || 0));
      const yearRange = maxYear - minYear;
      const pixelsPerYear = 100 / yearRange; // 假设100px代表1年
      const yearOffset = -newDragOffset / pixelsPerYear;
      const newSliderValue = ((yearOffset) / yearRange) * 100;
      setSliderValue(Math.max(0, Math.min(100, newSliderValue)));
    }
    
    setTouchEnd(currentX);
  }, [touchStart, lastDragX, dragOffset, filteredDynasties]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe || isRightSwipe) {
      // 可以在这里添加额外的滑动逻辑
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd]);

  // 鼠标拖拽事件处理
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setLastDragX(e.clientX);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - lastDragX;
    const newDragOffset = dragOffset + deltaX;
    setDragOffset(newDragOffset);
    setLastDragX(e.clientX);
    
    // 同步更新滑块
    if (filteredDynasties.length > 0) {
      const minYear = Math.min(...filteredDynasties.map(d => d.startYear || 0));
      const maxYear = Math.max(...filteredDynasties.map(d => d.endYear || 0));
      const yearRange = maxYear - minYear;
      const pixelsPerYear = 100 / yearRange;
      const yearOffset = -newDragOffset / pixelsPerYear;
      const newSliderValue = ((yearOffset) / yearRange) * 100;
      setSliderValue(Math.max(0, Math.min(100, newSliderValue)));
    }
  }, [isDragging, lastDragX, dragOffset, filteredDynasties]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 添加全局鼠标事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 绘制全景图
  useEffect(() => {
    drawOverview();
  }, [drawOverview]);

  const drawHorizontalTimeline = useCallback((g, width, height) => {
    if (!filteredDynasties.length) return;

    const isCompact = width < 560;
    const reservedBottomSpacePx = isCompact ? 20 : 190;
    const availableHeight = Math.max(200, height - reservedBottomSpacePx);
    const chartCenterY = availableHeight / 2;

    const minYear = Math.min(...filteredDynasties.map(d => d.startYear || 0));
    const maxYear = Math.max(...filteredDynasties.map(d => d.endYear || 0));
    const yearRange = maxYear - minYear;
    const requiredWidth = Math.max(width, yearRange / tickInterval * 100);

    const xScale = d3.scaleLinear()
      .domain([minYear, maxYear])
      .range([50, requiredWidth - 50]);

    const timelineGroup = g.append('g')
      .attr('transform', `translate(${dragOffset}, 0)`);

    const adjustedXScale = (year) => xScale(year);

    const labelTickInterval = isCompact ? Math.max(tickInterval * 5, 500) : tickInterval;
    const tickValues = [];
    for (let year = Math.ceil(minYear / labelTickInterval) * labelTickInterval; year <= maxYear; year += labelTickInterval) {
      tickValues.push(year);
    }

    const xAxis = d3.axisBottom(xScale)
      .tickValues(tickValues)
      .tickFormat(d => isCompact ? formatShortYear(d) : formatYear(d));

    timelineGroup.append('g')
      .attr('transform', `translate(0, ${chartCenterY + 50})`)
      .call(xAxis)
      .selectAll('text')
      .style('font-family', '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif')
      .style('font-size', isCompact ? '9px' : '12px')
      .style('font-weight', '500');

    const colorScale = d3.scaleOrdinal(Object.values(appleColors));

    const dynastyRects = timelineGroup.selectAll('.dynasty-rect')
      .data(filteredDynasties)
      .enter()
      .append('g')
      .attr('class', 'dynasty-rect')
      .style('cursor', 'pointer');

    const rowHeight = isCompact ? 42 : 50;
    const rowPadding = isCompact ? 6 : 10;
    const totalRowHeight = rowHeight + rowPadding;
    
    dynastyRects.append('rect')
      .attr('x', d => adjustedXScale(d.startYear))
      .attr('y', (d, i) => {
        const row = i % columnCount;
        return chartCenterY - (columnCount * totalRowHeight / 2) + (row * totalRowHeight) - 15;
      })
      .attr('width', d => Math.max(adjustedXScale(d.endYear) - adjustedXScale(d.startYear), 2))
      .attr('height', 30)
      .attr('fill', (d, i) => colorScale(i))
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1)
      .attr('rx', 4)
      .attr('ry', 4)
      .style('opacity', 0.9)
      .on('mouseover', function() {
        d3.select(this).style('opacity', 1).attr('stroke-width', 2);
      })
      .on('mouseout', function() {
        d3.select(this).style('opacity', 0.9).attr('stroke-width', 1);
      })
      .on('click', function(event, d) {
        setSelectedDynasty(d);
      });

    dynastyRects.append('text')
      .attr('x', d => {
        const rectWidth = adjustedXScale(d.endYear) - adjustedXScale(d.startYear);
        const isShortDynasty = rectWidth < 60;
        if (isShortDynasty) {
          return adjustedXScale(d.endYear) + 8;
        } else {
          return adjustedXScale(d.startYear) + rectWidth / 2;
        }
      })
      .attr('y', (d, i) => {
        const row = i % columnCount;
        return chartCenterY - (columnCount * totalRowHeight / 2) + (row * totalRowHeight);
      })
      .attr('text-anchor', d => {
        const rectWidth = adjustedXScale(d.endYear) - adjustedXScale(d.startYear);
        const isShortDynasty = rectWidth < 60;
        return isShortDynasty ? 'start' : 'middle';
      })
      .attr('dominant-baseline', 'middle')
      .style('font-family', '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif')
      .style('font-size', isCompact ? '10px' : '11px')
      .style('font-weight', '600')
      .style('fill', '#333333')
      .style('pointer-events', 'none')
      .text(d => d.name);

    const totalWidth = requiredWidth + 200;
    const drag = d3.drag()
      .on('drag', function(event) {
        const newOffset = dragOffset + event.dx;
        const minOffset = totalWidth > width ? Math.min(0, width - totalWidth) : -totalWidth * 0.5;
        const maxOffset = totalWidth > width ? 100 : totalWidth * 0.5;
        const clampedOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
        setDragOffset(clampedOffset);
      });

    g.call(drag);
  }, [filteredDynasties, dragOffset, setDragOffset, setSelectedDynasty, tickInterval, columnCount, formatYear, formatShortYear]);

  const drawFishboneTimeline = useCallback((g, width, height) => {
    const spacing = 120;
    const branchHeight = 80;
    const dynastyWidth = 100;
    const dynastyHeight = 30;
    const centerY = height / 2;
    
    const timelineGroup = g.append('g')
      .attr('transform', `translate(${dragOffset}, 0)`);

    timelineGroup.append('line')
      .attr('x1', 0)
      .attr('y1', centerY)
      .attr('x2', filteredDynasties.length * spacing)
      .attr('y2', centerY)
      .attr('stroke', '#000000')
      .attr('stroke-width', 3);

    const dynastyGroups = timelineGroup.selectAll('.dynasty-group')
      .data(filteredDynasties)
      .enter()
      .append('g')
      .attr('class', 'dynasty-group')
      .attr('transform', (d, i) => `translate(${i * spacing + 50}, ${centerY})`)
      .style('cursor', 'pointer');

    dynastyGroups.each(function(d, i) {
      const group = d3.select(this);
      const isUpper = i % 2 === 0;
      const branchY = isUpper ? -branchHeight : branchHeight;
      const rectY = isUpper ? -branchHeight - dynastyHeight / 2 : branchHeight - dynastyHeight / 2;

      group.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', branchY)
        .attr('stroke', '#000000')
        .attr('stroke-width', 2);

      group.append('rect')
        .attr('x', -dynastyWidth / 2)
        .attr('y', rectY)
        .attr('width', dynastyWidth)
        .attr('height', dynastyHeight)
        .attr('fill', '#4A90E2')
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('rx', 4)
        .attr('ry', 4)
        .style('opacity', 0.9)
        .style('cursor', 'pointer')
        .on('mouseover', function() {
          d3.select(this).style('opacity', 1).attr('stroke-width', 2);
        })
        .on('mouseout', function() {
          d3.select(this).style('opacity', 0.9).attr('stroke-width', 1);
        })
        .on('click', function() {
          setSelectedDynasty(d);
        });

      group.append('text')
        .attr('x', 0)
        .attr('y', rectY + dynastyHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .style('font-family', '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif')
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('fill', '#ffffff')
        .style('pointer-events', 'none')
        .text(d.name);

      group.append('text')
        .attr('x', 0)
        .attr('y', isUpper ? -branchHeight - dynastyHeight - 10 : branchHeight + dynastyHeight + 20)
        .attr('text-anchor', 'middle')
        .style('font-family', '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif')
        .style('font-size', '9px')
        .style('font-weight', '400')
        .style('fill', '#666666')
        .text(`${formatYear(d.startYear).replace('年','')}-${formatYear(d.endYear).replace('年','')}`);
    });
    
    const drag = d3.drag()
      .on('drag', function(event) {
        const newOffset = dragOffset + event.dx;
        const minOffset = Math.min(0, width - filteredDynasties.length * spacing - 100);
        const maxOffset = 100;
        const clampedOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
        setDragOffset(clampedOffset);
      });
    
    g.call(drag);
  }, [filteredDynasties, dragOffset, setDragOffset, setSelectedDynasty, formatYear]);



  const drawTimeline = useCallback(() => {
    if (!filteredDynasties.length || !svgRef.current) return;

    const svgNode = svgRef.current;
    const svg = d3.select(svgNode);
    svg.selectAll('*').remove();

    const width = svgNode.clientWidth;
    if (!width) return;

    let height;
    if (showFishbone) {
      height = width < 560 ? 300 : 400;
    } else {
      const rowHeight = 50;
      const rowPadding = 10;
      const totalRowHeight = rowHeight + rowPadding;
      const minHeight = width < 560 ? 280 : 400;
      height = Math.max(minHeight, columnCount * totalRowHeight + 100);
    }

    svg.attr('height', height);

    const g = svg.append('g');

    if (showFishbone) {
      drawFishboneTimeline(g, width, height);
    } else {
      drawHorizontalTimeline(g, width, height);
    }
  }, [filteredDynasties, showFishbone, columnCount, drawHorizontalTimeline, drawFishboneTimeline]);

  useEffect(() => {
    fetchDynasties();
  }, [fetchDynasties]);

  useEffect(() => {
    filterDynasties();
  }, [filterDynasties]);

  useEffect(() => {
    if (loading || error || hasRestoredSnapshotRef.current || !restoredState) return;

    hasRestoredSnapshotRef.current = true;
    const restoredDynasty = dynasties.find(
      dynasty => dynasty._id === restoredState.selectedDynastyId
    );

    if (restoredDynasty) {
      setSelectedDynasty(restoredDynasty);
    }

    if (Number.isFinite(restoredState.scrollY)) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: Math.max(0, restoredState.scrollY), behavior: 'auto' });
        });
      });
    }

    clearTimelineNavigationState();
  }, [dynasties, error, loading, restoredState]);

  useEffect(() => {
    if (!selectedDynasty || !filteredDynasties.length) return;

    const selectedIndex = filteredDynasties.findIndex(
      dynasty => dynasty._id === selectedDynasty._id
    );
    if (selectedIndex >= 0) {
      setCurrentDynastyIndex(selectedIndex);
    }
  }, [filteredDynasties, selectedDynasty]);

  useEffect(() => {
    drawTimeline();
  }, [drawTimeline]);

  const navigateToDynasty = (direction) => {
    if (!filteredDynasties.length) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentDynastyIndex + 1) % filteredDynasties.length;
    } else {
      newIndex = currentDynastyIndex === 0 ? filteredDynasties.length - 1 : currentDynastyIndex - 1;
    }
    
    setCurrentDynastyIndex(newIndex);
    setSelectedDynasty(filteredDynasties[newIndex]);
  };

  const viewDynastyDetails = () => {
    if (!selectedDynasty) return;

    const timelineSnapshot = {
      searchTerm,
      selectedPeriod,
      selectedDynastyId: selectedDynasty._id,
      currentDynastyIndex,
      dragOffset,
      tickInterval,
      showFishbone,
      columnCount,
      sliderValue,
      scrollY: window.scrollY
    };

    saveTimelineNavigationState(timelineSnapshot);
    navigate(`/dynasty/${selectedDynasty._id}`, {
      state: { fromTimeline: true, timelineSnapshot }
    });
  };

  if (loading) {
    return (
      <TimelineContainer>
        <Header>
          <HeaderContent>
            <Title>历史时间轴</Title>
            <Subtitle>探索历史文明的演进轨迹</Subtitle>
          </HeaderContent>
        </Header>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>正在加载历史数据...</LoadingText>
        </LoadingContainer>
      </TimelineContainer>
    );
  }

  if (error) {
    return (
      <TimelineContainer>
        <Header>
          <HeaderContent>
            <Title>历史时间轴</Title>
            <Subtitle>探索历史文明的演进轨迹</Subtitle>
          </HeaderContent>
        </Header>
        <ErrorMessage>
          <h3>加载失败</h3>
          <p>{error}</p>
          <button onClick={fetchDynasties}>重试</button>
        </ErrorMessage>
      </TimelineContainer>
    );
  }

  return (
    <TimelineContainer>
      <Header>
        <HeaderContent>
          <Title>历史时间轴</Title>
          <Subtitle>探索历史文明的演进轨迹</Subtitle>
        </HeaderContent>
      </Header>

      <Controls>
        <ControlsContent>
          <SearchBox>
            <Search className="search-icon" />
            <SearchInput
              type="text"
              placeholder="搜索条目名称或描述..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>

          <PeriodFilterGroup>
            {periods.map(period => (
              <FilterButton
                key={period.value}
                className={selectedPeriod === period.value ? 'active' : ''}
                onClick={() => setSelectedPeriod(period.value)}
              >
                {period.label}
              </FilterButton>
            ))}
          </PeriodFilterGroup>

          <SelectorGroup>
            <TickIntervalSelector>
              <label>刻度间隔:</label>
              <select
                value={tickInterval}
                onChange={(e) => setTickInterval(Number(e.target.value))}
              >
                <option value={50}>50年</option>
                <option value={100}>100年</option>
                <option value={200}>200年</option>
                <option value={500}>500年</option>
              </select>
            </TickIntervalSelector>

            {!showFishbone && (
              <TickIntervalSelector>
                <label>显示列数:</label>
                <select
                  value={columnCount}
                  onChange={(e) => setColumnCount(Number(e.target.value))}
                >
                  <option value={1}>1列</option>
                  <option value={2}>2列</option>
                  <option value={3}>3列</option>
                  <option value={4}>4列</option>
                  <option value={5}>5列</option>
                </select>
              </TickIntervalSelector>
            )}
          </SelectorGroup>

          <FishboneToggle
            className={showFishbone ? 'active' : ''}
            onClick={() => {
              setShowFishbone(!showFishbone);
            }}
          >
            <BarChart3 />
            {showFishbone ? '水平视图' : '鱼骨视图'}
          </FishboneToggle>

          {visibleRange && (
            <RangeSummary>
              当前视窗: {formatYear(visibleRange.start).replace('年','')} - {formatYear(visibleRange.end).replace('年','')}
            </RangeSummary>
          )}

          <NavigationButtons>
            <NavButton
              onClick={jumpToStart}
              disabled={!filteredDynasties.length}
            >
              起点
            </NavButton>
            <NavButton
              onClick={jumpToRecent}
              disabled={!filteredDynasties.length}
            >
              近代
            </NavButton>
            <NavButton
              onClick={() => navigateToDynasty('prev')}
              disabled={!filteredDynasties.length}
            >
              上一个
            </NavButton>
            <NavButton
              onClick={() => navigateToDynasty('next')}
              disabled={!filteredDynasties.length}
            >
              下一个
            </NavButton>
            <NavButton onClick={resetTimeline}>
              重置
            </NavButton>
          </NavigationButtons>
        </ControlsContent>
      </Controls>

      <TimelineSection>
        <TimelineContent>
          <TimelineVisualization 
            className={showFishbone ? 'fishbone' : ''}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            <StatusInfo>
              显示 {filteredDynasties.length} 个条目 | 刻度间隔: {tickInterval}年 {!showFishbone && `| 列数: ${columnCount}`}
            </StatusInfo>
            <svg ref={svgRef} className="timeline-svg" />
            <MobileTimelineHint>
              左右拖拽时间轴，或使用下方全景图和滑块快速定位。
            </MobileTimelineHint>
            
            {/* 全景图 - 移到图表内部 */}
            {!showFishbone && filteredDynasties.length > 0 && (
              <OverviewContainer>
                <OverviewLabel>历史全景图</OverviewLabel>
                <OverviewSvg ref={overviewRef} />
              </OverviewContainer>
            )}
            
            {/* 滑块控制器 - 移到图表内部 */}
            {!showFishbone && filteredDynasties.length > 0 && (
              <TimelineSliderContainer>
                <SliderTrack ref={sliderRef} onClick={handleTrackClick}>
                  <SliderProgress style={{ width: `${sliderValue}%` }} />
                  <SliderThumb 
                    style={{ left: `${sliderValue}%` }}
                    onMouseDown={handleSliderMouseDown}
                  />
                  <SliderLabels>
                    <span>
                      <span className="desktop-label">{formatYear(timelineRange.min)}</span>
                      <span className="mobile-label">{formatShortYear(timelineRange.min)}</span>
                    </span>
                    <span>
                      <span className="desktop-label">{formatYear(timelineRange.max)}</span>
                      <span className="mobile-label">{formatShortYear(timelineRange.max)}</span>
                    </span>
                  </SliderLabels>
                </SliderTrack>
                
                <SliderControlButtons>
                  <SliderButton 
                    onClick={() => handleSliderStep(-1)}
                    disabled={sliderValue <= 0}
                    title="向左移动"
                  >
                    ‹
                  </SliderButton>
                  <SliderButton 
                    onClick={() => handleSliderStep(1)}
                    disabled={sliderValue >= 100}
                    title="向右移动"
                  >
                    ›
                  </SliderButton>
                </SliderControlButtons>
              </TimelineSliderContainer>
            )}
          </TimelineVisualization>

          <AnimatePresence>
            {selectedDynasty && (
              <DynastyInfo
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="dynasty-header">
                  <div>
                    <h2>{selectedDynasty.name}</h2>
                    <p className="dynasty-period">
                      {formatYear(selectedDynasty.startYear).replace('年','')} - {formatYear(selectedDynasty.endYear).replace('年','')}
                      ({(selectedDynasty.endYear ?? 0) - (selectedDynasty.startYear ?? 0)}年)
                    </p>
                  </div>
                  <button 
                    className="close-button"
                    onClick={() => setSelectedDynasty(null)}
                  >
                    <X />
                  </button>
                </div>
                
                {selectedDynasty.description && (
                  <p>{selectedDynasty.description}</p>
                )}
                
                {selectedDynasty.majorEvents && selectedDynasty.majorEvents.length > 0 && (
                  <div className="dynasty-events">
                    <h4>重要事件</h4>
                    <ul>
                      {selectedDynasty.majorEvents.map((event, index) => (
                        <li key={index}>
                          <strong>{formatYear(event.year)}:</strong> {event.event || ''}{event.description ? ` — ${event.description}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="dynasty-actions">
                  <button 
                    className="action-button"
                    onClick={viewDynastyDetails}
                  >
                    <ExternalLink />
                    查看详情
                  </button>
                  <button 
                    className="action-button"
                    onClick={() => navigate('/quiz', { state: { dynasty: selectedDynasty } })}
                  >
                    <BookOpen />
                    相关测验
                  </button>
                </div>
              </DynastyInfo>
            )}
          </AnimatePresence>
        </TimelineContent>
      </TimelineSection>
    </TimelineContainer>
  );
}

export default Timeline;
